import React from "react";
import "./BlogDetails.css";
import Header from "../Header/header";

const BlogDetails = ({ blog }) => {
  // Sample blog data (in real app, this would come from props/API)
  const blogData = blog || {
    id: 1,
    title: "Getting Started with React: A Comprehensive Guide",
    author: "John Doe",
    date: "February 27, 2026",
    readTime: "5 min read",
    category: "React",
    image:
      "https://images.unsplash.com/photo-1633356122102-3fe601e05bd2?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
    content: [
      {
        type: "paragraph",
        text: "React has revolutionized the way we build user interfaces. In this comprehensive guide, we'll explore everything you need to know to get started with React development. From setting up your development environment to building your first component, we've got you covered.",
      },
      {
        type: "heading",
        text: "Why Choose React?",
      },
      {
        type: "paragraph",
        text: "React's component-based architecture makes it easy to build reusable UI components. Its virtual DOM ensures optimal performance, and the rich ecosystem of tools and libraries makes development a breeze. Whether you're building a small website or a large-scale application, React provides the flexibility and power you need.",
      },
      {
        type: "heading",
        text: "Setting Up Your Development Environment",
      },
      {
        type: "paragraph",
        text: "To get started with React, you'll need Node.js and npm installed on your computer. Once you have these prerequisites, you can create a new React app using Create React App, which sets up everything you need to start building.",
      },
      {
        type: "code",
        text: "npx create-react-app my-first-react-app\ncd my-first-react-app\nnpm start",
      },
      {
        type: "heading",
        text: "Understanding Components",
      },
      {
        type: "paragraph",
        text: "Components are the building blocks of any React application. They can be functional or class-based, and they manage their own state and props. Here's a simple example of a functional component:",
      },
      {
        type: "code",
        text: "function Welcome(props) {\n  return <h1>Hello, {props.name}</h1>;\n}",
      },
      {
        type: "heading",
        text: "Conclusion",
      },
      {
        type: "paragraph",
        text: "React is an powerful library that continues to dominate the frontend development landscape. With its simplicity, performance, and extensive community support, it's an excellent choice for your next project. Start building today!",
      },
    ],
    tags: ["React", "JavaScript", "Web Development", "Tutorial"],
    comments: [
      {
        id: 1,
        author: "Jane Smith",
        avatar: "https://i.pravatar.cc/150?img=1",
        date: "February 26, 2026",
        content: "Great article! Very helpful for beginners.",
        likes: 12,
      },
      {
        id: 2,
        author: "Mike Johnson",
        avatar: "https://i.pravatar.cc/150?img=2",
        date: "February 25, 2026",
        content:
          "Thanks for sharing. Looking forward to more content like this.",
        likes: 8,
      },
    ],
  };

  return (
    <div className="blog-details-container">
      <Header />
      {/* Header Section */}
      <header className="blog-header">
        <div className="blog-header-content">
          <div className="blog-category">{blogData.category}</div>
          <h1 className="blog-title">{blogData.title}</h1>

          <div className="blog-meta">
            <div className="author-info">
              <img
                src="https://i.pravatar.cc/150?img=3"
                alt={blogData.author}
                className="author-avatar"
              />
              <div className="author-details">
                <span className="author-name">{blogData.author}</span>
                <span className="post-date">{blogData.date}</span>
              </div>
            </div>
            <div className="read-time">
              <svg
                className="clock-icon"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm0 18c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8z" />
                <path d="M13 7h-2v6h6v-2h-4z" />
              </svg>
              {blogData.readTime}
            </div>
          </div>
        </div>

        <div className="blog-featured-image">
          <img src={blogData.image} alt={blogData.title} />
        </div>
      </header>

      {/* Main Content */}
      <main className="blog-main-content">
        <div className="content-wrapper">
          <article className="blog-article">
            {blogData.content.map((item, index) => {
              switch (item.type) {
                case "heading":
                  return (
                    <h2 key={index} className="article-heading">
                      {item.text}
                    </h2>
                  );
                case "code":
                  return (
                    <pre key={index} className="code-block">
                      <code>{item.text}</code>
                    </pre>
                  );
                default:
                  return (
                    <p key={index} className="article-paragraph">
                      {item.text}
                    </p>
                  );
              }
            })}
          </article>

          {/* Tags Section */}
          <div className="tags-section">
            <h3 className="section-title">Tags</h3>
            <div className="tags-container">
              {blogData.tags.map((tag, index) => (
                <span key={index} className="tag">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Share Section */}
          <div className="share-section">
            <h3 className="section-title">Share this article</h3>
            <div className="share-buttons">
              <button className="share-btn twitter">
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path d="M23.44 4.83c-.8.37-1.5.38-2.22.02.93-.56.98-.96 1.32-2.02-.88.52-1.86.9-2.9 1.1-.82-.88-2-1.43-3.3-1.43-2.5 0-4.55 2.05-4.55 4.55 0 .36.03.7.1 1.04-3.8-.2-7.17-2-9.42-4.76-.4.68-.6 1.46-.6 2.3 0 1.58.8 2.97 2.02 3.78-.74-.02-1.44-.23-2.05-.57v.06c0 2.2 1.56 4.03 3.64 4.45-.38.1-.78.16-1.2.16-.3 0-.58-.03-.86-.08.58 1.8 2.26 3.1 4.25 3.14-1.56 1.22-3.52 1.95-5.66 1.95-.37 0-.73-.02-1.1-.07 2.03 1.3 4.44 2.06 7.04 2.06 8.45 0 13.07-7 13.07-13.07v-.6c.9-.66 1.68-1.47 2.3-2.4z" />
                </svg>
                Twitter
              </button>
              <button className="share-btn facebook">
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.8c4.56-.93 8-4.96 8-9.8z" />
                </svg>
                Facebook
              </button>
              <button className="share-btn linkedin">
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.35V9h3.42v1.56h.05c.48-.9 1.65-1.85 3.4-1.85 3.63 0 4.3 2.39 4.3 5.5v6.24zM5.34 7.43c-1.14 0-2.06-.92-2.06-2.06 0-1.14.92-2.06 2.06-2.06 1.14 0 2.06.92 2.06 2.06 0 1.14-.92 2.06-2.06 2.06zM7 20.45H3.66V9H7v11.45z" />
                </svg>
                LinkedIn
              </button>
            </div>
          </div>

          {/* Comments Section */}
          <div className="comments-section">
            <h3 className="section-title">
              Comments ({blogData.comments.length})
            </h3>

            {/* Add Comment Form */}
            <div className="add-comment">
              <textarea placeholder="Add a comment..." rows="3"></textarea>
              <button className="post-comment-btn">Post Comment</button>
            </div>

            {/* Comments List */}
            <div className="comments-list">
              {blogData.comments.map((comment) => (
                <div key={comment.id} className="comment">
                  <img
                    src={comment.avatar}
                    alt={comment.author}
                    className="comment-avatar"
                  />
                  <div className="comment-content">
                    <div className="comment-header">
                      <span className="comment-author">{comment.author}</span>
                      <span className="comment-date">{comment.date}</span>
                    </div>
                    <p className="comment-text">{comment.content}</p>
                    <div className="comment-actions">
                      <button className="like-btn">
                        <svg viewBox="0 0 24 24" width="16" height="16">
                          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                        </svg>
                        {comment.likes}
                      </button>
                      <button className="reply-btn">Reply</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="blog-sidebar">
          {/* <div className="sidebar-widget author-widget">
            <h4 className="widget-title">About the Author</h4>
            <div className="author-card">
              <img 
                src="https://i.pravatar.cc/150?img=3" 
                alt={blogData.author}
                className="author-large-avatar"
              />
              <h5 className="author-full-name">{blogData.author}</h5>
              <p className="author-bio">
                Senior Developer Advocate with 8+ years of experience in web technologies. 
                Passionate about teaching and sharing knowledge with the developer community.
              </p>
            </div>
          </div> */}

          <div className="sidebar-widget related-posts">
            <h4 className="widget-title">Related Posts</h4>
            <div className="related-posts-list">
              <div className="related-post-blog">
                <a href="/">
                  <img
                    src="https://static.vecteezy.com/system/resources/thumbnails/049/855/471/small/nature-background-high-resolution-wallpaper-for-a-serene-and-stunning-view-free-photo.jpg"
                    alt=""
                  />
                  <p>React Hooks: A Complete Guide</p>
                  <span className="post-meta">5 min read</span>
                </a>
              </div>
              <div className="related-post-blog">
                <a href="/">
                  <img
                    src="https://static.vecteezy.com/system/resources/thumbnails/049/855/471/small/nature-background-high-resolution-wallpaper-for-a-serene-and-stunning-view-free-photo.jpg"
                    alt=""
                  />
                  <p>React Hooks: A Complete Guide</p>
                  <span className="post-meta">5 min read</span>
                </a>
              </div>
              <div className="related-post-blog">
                <a href="/">
                  <img
                    src="https://static.vecteezy.com/system/resources/thumbnails/049/855/471/small/nature-background-high-resolution-wallpaper-for-a-serene-and-stunning-view-free-photo.jpg"
                    alt=""
                  />
                  <p>React Hooks: A Complete Guide</p>
                  <span className="post-meta">5 min read</span>
                </a>
              </div>
            </div>
          </div>

          {/* <div className="sidebar-widget newsletter">
            <h4 className="widget-title">Newsletter</h4>
            <p>Get the latest posts delivered right to your inbox.</p>
            <input 
              type="email" 
              placeholder="Your email address"
              className="newsletter-input"
            />
            <button className="subscribe-btn">Subscribe</button>
          </div> */}
        </aside>
      </main>
    </div>
  );
};

export default BlogDetails;
